import {
  CheckOutlined,
  EditOutlined,
  FilePdfOutlined,
  LeftOutlined,
} from "@ant-design/icons";
import { Button, Col, Divider, Row, type ButtonProps } from "antd";
import Link from "next/link";
import Router from "next/router";
import { useState, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";

import { useAgentContext } from "../../agents/AgentContext";
import { SubmitButton } from "../../form/SubmitButton";

import styles from "./BottomPanel.module.css";

interface BottomPanelProps {
  sticky?: boolean;
  buttons: Array<ReactNode | false | null | undefined>;
}

export function BottomPanel({ buttons, sticky = true }: BottomPanelProps) {
  const { agentSidebarWidth } = useAgentContext();

  return (
    <div
      className={sticky ? styles.sticky : undefined}
      style={{ marginRight: agentSidebarWidth }}
    >
      {!sticky && <Divider dashed />}
      <Row justify="end" gutter={8} wrap={false}>
        {buttons.filter(Boolean).map((b, index) => (
          <Col key={`btn-${index}`}>{b}</Col>
        ))}
      </Row>
    </div>
  );
}

interface ButtonRouteProps extends ButtonProps {
  route: string;
  anchor?: string;
}

BottomPanel.CancelButton = ({ route, ...props }: ButtonRouteProps) => (
  <Button onClick={() => Router.push(route)} {...props}>
    <span>
      <FormattedMessage id="cancel" defaultMessage="cancel" />
    </span>
  </Button>
);

BottomPanel.SubmitButton = (props?: ButtonProps) => (
  <SubmitButton type="primary" {...props}>
    <span>
      <FormattedMessage id="save" defaultMessage="save" />
    </span>
  </SubmitButton>
);

BottomPanel.CompleteButton = (props?: ButtonProps) => (
  <SubmitButton
    type="primary"
    icon={<CheckOutlined />}
    className="btn-complete"
    {...props}
  />
);

BottomPanel.ReturnButton = ({ route }: ButtonRouteProps) => (
  <Link href={route} legacyBehavior>
    <Button type="default" icon={<LeftOutlined />} href={route}>
      <span>
        <FormattedMessage id="returnToIndex" defaultMessage="returnToIndex" />
      </span>
    </Button>
  </Link>
);

BottomPanel.EditButton = ({ route, anchor }: ButtonRouteProps) => {
  const href = [route, anchor].filter((p) => p).join("#");

  return (
    <Link href={href} legacyBehavior>
      <Button type="default" icon={<EditOutlined />} href={href}>
        <span>
          <FormattedMessage id="edit" defaultMessage="edit" />
        </span>
      </Button>
    </Link>
  );
};

BottomPanel.DownloadPDFButton = ({
  route,
  children,
  ...props
}: { route?: string } & ButtonProps) => (
  <Button
    type="default"
    icon={<FilePdfOutlined />}
    href={route}
    target="_blank"
    {...props}
  >
    <span className="hide-xs">
      {children || (
        <FormattedMessage id="downloadPdf" defaultMessage="downloadPdf" />
      )}
    </span>
  </Button>
);

const ActionButton = ({
  content,
  onClick,
  disable = true,
  ...props
}: Omit<ButtonProps, "content"> & {
  content: ReactNode;
  disable?: boolean;
}) => {
  const [disabled, setDisabled] = useState(false);

  return (
    <Button
      type="primary"
      icon={<CheckOutlined />}
      variant="solid"
      disabled={disabled}
      onClick={(event) => {
        if (!onClick) return;

        if (disable) setDisabled(true);
        onClick(event);
      }}
      {...props}
    >
      <span>{content}</span>
    </Button>
  );
};
BottomPanel.ActionButton = ActionButton;
